import React, { useState, useEffect } from 'react';
import Sidebar from "../../components/Sidebar/Sidebar";
import Footer from "../../components/Footer/Footer";
import { getUserProfile, updateUserProfile } from '../../api/userProfileApi';
import { fetchHealthData, updateHealthData } from '../../api/healthDataApi';
import { useLocation, useNavigate } from 'react-router-dom';
import ConnectedServices from '../../components/Settings/ConnectedServices';
import { createMedicalCondition, getMedicalConditions, updateMedicalCondition } from '../../api/medicalConditionApi';
import { createMedicalHistory, getMedicalHistory, updateMedicalHistory } from '../../api/medicalHistoryApi';
import { createSurgery, getSurgeries, updateSurgery } from '../../api/surgeryApi';

export default function Settings() {
    const location = useLocation();
    
    const params = new URLSearchParams(location.search);

    const defaultTab = params.get("tab") || "account";
    const [activeTab, setActiveTab] = useState(defaultTab);

    const [sidebarExpanded, setSidebarExpanded] = useState(true);
    const tabs = [
        'Account',
        'Security',
        'Connected Services',
        'Health Data',
        'Care Info',
        'App Settings',
    ];

    // Form data state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
        age: '',
        sex: '',
        height_cm: '',
        weight_lbs: '',
        blood_type: '',
        insurance_info: '',
        pharmacy_info: '',
        emergency_name: '',
        emergency_phone: '',
        emergency_relation: '',
    });

    const [healthData, setHealthData] = useState({
        allergies: '',
        family_history: '',
    })

    const [medicalConditions, setMedicalConditions] = useState([]);
    const [surgeries, setSurgeries] = useState([]);
    const [medicalHistory, setMedicalHistory] = useState([]);

    const userId = localStorage.getItem("userId");

    const navigate = useNavigate();

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0]; // "YYYY-MM-DD"
    };

    useEffect(() => {
        if (!userId) {
            return;
        }

        const loadProfile = async () => {
            const data = await getUserProfile(userId);
            setFormData(data);
        };
        loadProfile();
    }, [userId]);

    useEffect(() => {
        if (!userId) {
            return;
        }

        const loadHealthData = async () => {
            const data = await fetchHealthData(userId);
            setHealthData(data);
        }

        loadHealthData();
    }, [userId])

    useEffect(() => {
        if (!userId) {
            return;
        }

        const fetchHealthData = async () => {
            const conds = await getMedicalConditions(userId);

            const formattedConditions = conds.map(c => ({
                ...c,
                diagnosis_date: formatDateForInput(c.diagnosis_date)
            }));

            const surg = await getSurgeries(userId);

            const formattedSurgeries = surg.map(s => ({
                ...s,
                surgery_date: formatDateForInput(s.surgery_date)
            }));

            const medicalHistory = await getMedicalHistory(userId);

            const formattedHistory = medicalHistory.map(h => ({
                ...h,
                diagnosis_date: formatDateForInput(h.diagnosis_date)
            }));
            setMedicalHistory(formattedHistory);
            setMedicalConditions(formattedConditions);
            setSurgeries(formattedSurgeries);
        };

        fetchHealthData();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleHealthDataChange = (e) => {
        const { name, value } = e.target;
        setHealthData((prev) => ({ ...prev, [name]: value }));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        await updateUserProfile(userId, formData);
        alert("Profile saved!");
    };

    const handleHealthSubmit = async () => {
        try {
            await Promise.all(medicalConditions.map(cond =>
                cond.id
                    ? updateMedicalCondition(cond.id, cond)
                    : createMedicalCondition({ ...cond, user_id: userId })
            ));

            await Promise.all(surgeries.map(surg =>
                surg.id
                    ? updateSurgery(surg.id, surg)
                    : createSurgery({ ...surg, user_id: userId })
            ));

            await Promise.all(medicalHistory.map(hist =>
                hist.id
                    ? updateMedicalHistory(hist.id, hist)
                    : createMedicalHistory({ ...hist, user_id: userId })
            ));

            await updateHealthData(userId, healthData);

            alert("Health data saved!");
        } catch (err) {
            console.error(err);
            alert("Error saving health data.");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("userId");
        navigate("/login");
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'account':
                return (
                    <div className="pt-4 pb-8 space-y-6 border-b">
                        <div className="flex items-center justify-between">
                            <h1 className="py-2 text-2xl font-semibold">Personal Information</h1>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition"
                            >
                                Save
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">Full Name</label>
                                <input
                                    type="text"
                                    placeholder="Your Name"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    name="full_name"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">Email Address</label>
                                <input
                                    type="email"
                                    placeholder="email@example.com"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.email}
                                    onChange={handleChange}
                                    name="email"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">Phone Number</label>
                                <input
                                    type="tel"
                                    placeholder="(123) 456-7890"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    name="phone_number"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Age</label>
                                <input
                                    type="number"
                                    min="0"
                                    placeholder="Enter your age"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.age}
                                    onChange={handleChange}
                                    name="age"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Sex</label>
                                <select className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.sex} onChange={handleChange} name="sex">
                                    <option value="">Select</option>
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Other</option>
                                    <option>Prefer not to say</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Height (cm)</label>
                                <input
                                    type="number"
                                    placeholder="170"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.height_cm}
                                    onChange={handleChange}
                                    name="height_cm"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Weight (lbs)</label>
                                <input
                                    type="number"
                                    placeholder="130"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.weight_lbs}
                                    onChange={handleChange}
                                    name="weight_lbs"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Blood Type</label>
                                <select className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.blood_type} onChange={handleChange} name="blood_type">
                                    <option value="">Select</option>
                                    <option>A+</option>
                                    <option>A−</option>
                                    <option>B+</option>
                                    <option>B−</option>
                                    <option>AB+</option>
                                    <option>AB−</option>
                                    <option>O+</option>
                                    <option>O−</option>
                                </select>
                            </div>
                        </div>

                        <div className="pt-10 mt-10 border-t">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">Account Actions</h2>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition"
                            >
                                Log Out
                            </button>
                        </div>

                    </div>
                );
            case 'security':
                return (
                    <div className="pt-4 pb-8 space-y-8 border-b">
                        <h1 className="py-2 text-2xl font-semibold">Security Settings</h1>

                        {/* change password */}
                        <div className="space-y-4">
                            <h2 className="text-xl font-semibold text-slate-800">Change Password</h2>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-slate-700">Current Password</label>
                                    <input
                                        type="password"
                                        className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-slate-700">New Password</label>
                                    <input
                                        type="password"
                                        className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <button className="px-4 py-2 mt-2 text-white bg-blue-600 rounded hover:bg-blue-700">
                                Update Password
                            </button>
                        </div>

                        {/* 2FA */}
                        <div className="space-y-2">
                            <h2 className="text-xl font-semibold text-slate-800">Two-Factor Authentication (2FA)</h2>
                            <p className="text-slate-600">Add an extra layer of security to your account.</p>
                            <label className="inline-flex items-center mt-2 cursor-pointer">
                                <input type="checkbox" value="" class="sr-only peer" />
                                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable 2FA</span>
                            </label>
                        </div>

                        {/* SSO and connected accounts */}
                        <div className="space-y-2">
                            <h2 className="text-xl font-semibold text-slate-800">Connected Accounts</h2>
                            <p className="text-slate-600">Manage your single sign-on options.</p>
                            <ul className="mt-3 space-y-3">
                                {['Google', 'GitHub', 'Microsoft'].map((provider) => (
                                    <li key={provider} className="flex items-center justify-between px-4 py-3 bg-white border rounded shadow-sm">
                                        <span className="font-medium text-slate-800">{provider}</span>
                                        <button className="text-sm text-red-600 hover:underline">Disconnect</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                );
            case 'health data':
                return (
                    <div className="pt-4 pb-8 space-y-6 border-b">
                        <div className="flex items-center justify-between">
                            <h1 className="py-2 text-2xl font-semibold">Health Data Management</h1>
                            <button
                                onClick={handleHealthSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition"
                            >
                                Save
                            </button>
                        </div>

                        <div className="space-x-5 space-y-2">
                            <h2 className="text-lg font-medium text-slate-800">Data Sharing Preferences</h2>

                            <label className="inline-flex items-center mt-2 cursor-pointer">
                                <input type="checkbox" value="" class="sr-only peer" />
                                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Allow to share health data with third-party platforms?</span>
                            </label>

                            <label className="inline-flex items-center mt-2 cursor-pointer">
                                <input type="checkbox" value="" class="sr-only peer" />
                                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300"> Allow Welby to use health data?</span>
                            </label>
                        </div>

                        <div>
                            <label className="block mb-3 text-sm font-medium text-slate-700">
                                Medical Conditions
                            </label>

                            <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-slate-700">
                                <span className="flex-grow">Condition Name</span>
                                <span className="w-40">Diagnosis Date</span>
                                <span className="w-10"></span>
                            </div>
                            <div className="space-y-3">
                                {medicalConditions.map((cond, idx) => (
                                    <div key={cond.id || idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={cond.condition_name}
                                            onChange={(e) => {
                                                const updated = [...medicalConditions];
                                                updated[idx].condition_name = e.target.value;
                                                setMedicalConditions(updated);
                                            }}
                                            className="flex-grow px-2 py-1 border rounded"
                                            placeholder="Condition name"
                                        />
                                        <input
                                            type="date"
                                            value={cond.diagnosis_date || ""}
                                            onChange={(e) => {
                                                const updated = [...medicalConditions];
                                                updated[idx].diagnosis_date = e.target.value;
                                                setMedicalConditions(updated);
                                            }}
                                            className="w-40 px-2 py-1 border rounded"
                                        />
                                        <button
                                            onClick={() => setMedicalConditions(medicalConditions.filter((_, i) => i !== idx))}
                                            className="px-3 py-1 bg-red-500 text-white rounded"
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={() =>
                                        setMedicalConditions([...medicalConditions, { condition_name: "", diagnosis_date: "" }])
                                    }
                                    className="px-3 py-1 bg-blue-500 text-white rounded"
                                >
                                    + Add Condition
                                </button>
                            </div>

                        </div>

                        <div>
                            <label className="block mb-3 text-sm font-medium text-slate-700">
                                Medical History
                            </label>

                            <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-slate-700">
                                <span className="flex-grow">Condition Name</span>
                                <span className="w-40">Diagnosis Date</span>
                                <span className="w-10"></span>
                            </div>
                            <div className="space-y-3">
                                {medicalHistory.map((cond, idx) => (
                                    <div key={cond.id || idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={cond.condition_name}
                                            onChange={(e) => {
                                                const updated = [...medicalHistory];
                                                updated[idx].condition_name = e.target.value;
                                                setMedicalHistory(updated);
                                            }}
                                            className="flex-grow px-2 py-1 border rounded"
                                            placeholder="Condition name"
                                        />
                                        <input
                                            type="date"
                                            value={cond.diagnosis_date || ""}
                                            onChange={(e) => {
                                                const updated = [...medicalHistory];
                                                updated[idx].diagnosis_date = e.target.value;
                                                setMedicalHistory(updated);
                                            }}
                                            className="w-40 px-2 py-1 border rounded"
                                        />
                                        <button
                                            onClick={() => setMedicalHistory(medicalHistory.filter((_, i) => i !== idx))}
                                            className="px-3 py-1 bg-red-500 text-white rounded"
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={() =>
                                        setMedicalHistory([...medicalHistory, { condition_name: "", diagnosis_date: "" }])
                                    }
                                    className="px-3 py-1 bg-blue-500 text-white rounded"
                                >
                                    + Add Condition
                                </button>
                            </div>

                        </div>

                        <div>
                            <label className="block mb-3 text-sm font-medium text-slate-700">
                                Surgeries
                            </label>

                            <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-slate-700">
                                <span className="flex-grow">Surgery Name</span>
                                <span className="w-40">Surgery Date</span>
                                <span className="w-10"></span>
                            </div>
                            <div className="space-y-3">
                                {surgeries.map((cond, idx) => (
                                    <div key={cond.id || idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={cond.surgery_name}
                                            onChange={(e) => {
                                                const updated = [...surgeries];
                                                updated[idx].surgery_name = e.target.value;
                                                setSurgeries(updated);
                                            }}
                                            className="flex-grow px-2 py-1 border rounded"
                                            placeholder="Surgery name"
                                        />
                                        <input
                                            type="date"
                                            value={cond.surgery_date || ""}
                                            onChange={(e) => {
                                                const updated = [...surgeries];
                                                updated[idx].surgery_date = e.target.value;
                                                setSurgeries(updated);
                                            }}
                                            className="w-40 px-2 py-1 border rounded"
                                        />
                                        <button
                                            onClick={() => setSurgeries(surgeries.filter((_, i) => i !== idx))}
                                            className="px-3 py-1 bg-red-500 text-white rounded"
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={() =>
                                        setSurgeries([...surgeries, { surgery_name: "", surgery_date: "" }])
                                    }
                                    className="px-3 py-1 bg-blue-500 text-white rounded"
                                >
                                    + Add Surgery
                                </button>
                            </div>

                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-slate-700">Family History</label>
                            <textarea
                                rows="3"
                                placeholder="E.g., Hypertension, Heart disease"
                                className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={healthData.family_history}
                                name="family_history"
                                onChange={handleHealthDataChange}
                            ></textarea>
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-slate-700">Allergies</label>
                            <textarea
                                rows="3"
                                placeholder="E.g., Penicillin, Peanuts"
                                className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={healthData.allergies}
                                name="allergies"
                                onChange={handleHealthDataChange}
                            ></textarea>
                        </div>
                    </div>
                );

            case 'app settings':
                return (
                    <div className="pt-4 pb-8 space-y-10 border-b">
                        <h1 className="py-2 text-2xl font-semibold">App Settings</h1>
                        <h2 className="text-xl font-semibold text-slate-800">Notification Settings</h2>
                        <section className="space-y-4">
                            {['Comments', 'Reminders', 'Updates'].map((section) => (
                                <div key={section} className="grid py-6 border-b sm:grid-cols-2">
                                    <div>
                                        <h2 className="text-lg font-semibold leading-4 text-slate-700">{section}</h2>
                                        <p className="text-slate-600">Customize your {section.toLowerCase()} alerts.</p>
                                    </div>
                                    <div className="flex items-center mt-4 sm:justify-end">
                                        <div className="flex flex-col gap-3">
                                            {['Push', 'Email', 'SMS'].map((type) => (
                                                <label key={type} className="relative inline-flex items-center cursor-pointer">
                                                    <input type="checkbox" className="sr-only peer" defaultChecked={type !== 'SMS'} />
                                                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300" />
                                                    <span className="ml-3 text-sm font-medium text-gray-900">{type}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-xl font-semibold text-slate-800">Privacy Settings</h2>
                            <p className="text-slate-600">Manage your data sharing permissions and review our privacy policy.</p>

                            <div className="space-x-5">
                                <label className="inline-flex items-center mt-2 cursor-pointer">
                                    <input type="checkbox" value="" class="sr-only peer" />
                                    <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                    <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Allow app to collect usage data for improvements?</span>
                                </label>

                                <label className="inline-flex items-center mt-2 cursor-pointer">
                                    <input type="checkbox" value="" class="sr-only peer" />
                                    <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                    <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Share anonymized data with research partners?</span>
                                </label>
                            </div>

                            <div className="mt-4">
                                <a href="#" className="text-sm text-blue-600 hover:underline">
                                    View Privacy Policy
                                </a>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-xl font-semibold text-slate-800">Welby Settings</h2>
                            <p className="text-slate-600">Customize how Welby engages with you.</p>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">Interaction Frequency</label>
                                <select className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                    <option>Only when necessary</option>
                                    <option>Once per day</option>
                                    <option>Multiple times per day</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">Level of Detail</label>
                                <select className="w-full px-4 py-2 border rounded-md shadow-sm">
                                    <option>Summary only</option>
                                    <option>Moderate detail</option>
                                    <option>Full explanations</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-slate-700">General Push Notifications</label>
                                <select className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                    <option>Daily</option>
                                    <option>Weekly</option>
                                </select>
                            </div>
                        </section>
                    </div>
                );
            case 'care info':
                return (
                    <div>
                        <div className="pt-4 pb-8 space-y-5 border-b">
                            <div className="flex items-center justify-between">
                                <h1 className="py-2 text-2xl font-semibold">Care Info Settings</h1>
                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition"
                                >
                                    Save
                                </button>
                            </div>

                            <div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">Insurance Information</h2>
                                <input
                                    type="text"
                                    placeholder="Provider and Policy Number"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.insurance_info}
                                    onChange={handleChange}
                                    name="insurance_info"
                                />
                            </div>

                            <div className="pt-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">Preferred Pharmacy</h2>
                                <input
                                    type="text"
                                    placeholder="Pharmacy name and address"
                                    className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={formData.pharmacy_info}
                                    onChange={handleChange}
                                    name="pharmacy_info"
                                />
                            </div>

                            <div className="pt-6">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">Emergency Contact</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <input
                                        type="text"
                                        placeholder="Full Name"
                                        className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={formData.emergency_name}
                                        onChange={handleChange}
                                        name="emergency_name"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Phone Number"
                                        className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={formData.emergency_phone}
                                        onChange={handleChange}
                                        name="emergency_phone"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Relationship"
                                        className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={formData.emergency_relation}
                                        onChange={handleChange}
                                        name="emergency_relation"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case 'connected services':
                return <ConnectedServices />;
            default:
                return (
                    <div className="py-10 text-gray-700">
                        <h2 className="text-2xl font-semibold capitalize">{activeTab} Settings</h2>
                        <p className="mt-2 text-slate-500">placeholder</p>
                    </div>
                );
        }
    };

    return (
        <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>
            <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

            <main className="flex-1 p-6 overflow-auto bg-gray-100">
                <h1 className="py-6 text-4xl font-semibold text-[#04395b]">Settings</h1>
                <div className="grid grid-cols-8 sm:grid-cols-10">
                    {/* mobile dropdown */}
                    <div className="relative w-56 my-4 sm:hidden">
                        <input className="hidden peer" type="checkbox" id="select-tab" />
                        <label htmlFor="select-tab" className="flex w-full p-2 px-3 text-sm text-gray-700 border rounded-lg cursor-pointer select-none ring-blue-700 peer-checked:ring">
                            {activeTab}
                        </label>
                        <svg className="absolute right-0 h-4 ml-auto mr-5 transition pointer-events-none top-3 text-slate-700 peer-checked:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                        <ul className="flex-col overflow-hidden transition-all duration-300 bg-white rounded-b-lg shadow-md select-none max-h-0 peer-checked:max-h-56 peer-checked:py-3">
                            {tabs.map((tab) => (
                                <li
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab.toLowerCase());
                                        document.getElementById('select-tab').checked = false;
                                    }}
                                    className="px-3 py-2 text-sm cursor-pointer text-slate-600 hover:bg-blue-700 hover:text-white"
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* sidebar navigation */}
                    <div className="hidden col-span-2 sm:block">
                        <ul>
                            {tabs.map((tab) => (
                                <li
                                    key={tab}
                                    onClick={() => setActiveTab(tab.toLowerCase())}
                                    className={`mt-5 cursor-pointer border-l-2 px-2 py-2 font-semibold transition ${activeTab === tab.toLowerCase()
                                        ? 'border-l-blue-700 text-blue-700'
                                        : 'border-transparent hover:border-l-blue-700 hover:text-blue-700'
                                        }`}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* dynamically render every tab */}
                    <div className="col-span-8 px-8 shadow rounded-xl bg-gray-50">
                        {renderContent()}
                    </div>

                </div>
            </main>
            <Footer />
        </div>
    );
}
